import { retrieveFeaturedList } from '@/api/featured'
import { createSharedComposable, useEventListener } from '@vueuse/core'
import { metaPixelTrack } from '@/utils/utils'

/**
 * @returns {object}
 * filter - 返回过滤项配置
 * loading - 当前是否处于加载中
 * finished - 是否已加载完成，加载完成后不再触发 onLoad 事件
 * list - 返回列表数据
 * onLoad - 加载更多
 * onReset - 重置 page / loading / finished 状态并触发 onLoad 事件
 */
export const useProductList = createSharedComposable(() => {
  const route = useRoute()

  const loading = ref(false)
  const finished = ref(false)

  const filter = reactive({
    category: route.params.category,
    seriesId: route.params.collectionId,
    keyword: null,
    sortMap: null
  })
  const page = reactive({
    page: 1,
    pageSize: 50
  })

  const list = shallowRef([])

  useEventListener(window, 'scroll', (e) => {
    if (loading.value || finished.value) return
    const el = e.target.scrollingElement
    if (el.scrollHeight - el.clientHeight - el.scrollTop < 75) {
      onLoad()
    }
  })

  /**
   * 防止请求竞态导致列表数据展示不正确问题
   */
  let latestRequestId = 0

  const onLoad = async () => {
    if (loading.value || finished.value) return
    try {
      const currentRequestId = ++latestRequestId
      const { keyword, ...rest } = filter
      const data = { searchKeyWord: keyword, ...rest, ...page }
      loading.value = true
      const rows = await getProductList(data)

      if (currentRequestId === latestRequestId) {
        if (rows.length === 0) {
          finished.value = true
        } else {
          list.value = [...list.value, ...rows]
          page.page++
        }
        /**
         * 不用 finally 修改 loading 状态是因为 当 请求竞态 出现时，finally 会导致 loading 状态不正确
         */
        loading.value = false
      }
    } catch (e) {
      console.error(e)
      loading.value = false
    }
  }

  const getProductList = async (filter) => {
    const currencyData = JSON.parse(localStorage.getItem('currencyData'))
    const { code, data, msg } = await retrieveFeaturedList({
      ...filter,
      currency: currencyData?.targetCurrency ?? 'USD'
    })
    if (code !== 200) {
      throw new Error(msg)
    }
    /**
     * Meta Pixel 埋点：Featured搜索完成
     */
    if (filter.page === 1 && filter.searchKeyWord !== '' && filter.searchKeyWord != null) {
      metaPixelTrack('PFS401', 'Search', {
        content_ids: data.slice(0, 10).map((item) => item.offerId),
        content_type: 'product_group',
        currency: '$',
        value: 0.1,
        search_string: filter.searchKeyWord
      })
    }
    return data
  }

  const onReset = () => {
    page.page = 1
    list.value = []
    loading.value = false
    finished.value = false
    onLoad()
  }

  onBeforeMount(() => {
    onLoad()
  })

  return { filter, loading, finished, list, onLoad, onReset }
})
